// Portfolio Atlas — "one map of everything the portfolio knows", per
// screen5-atlas.png (Story #5115 Stream 6f, Task #5131). Self-contained like
// SynergyMatrix.tsx: resolves its own aggregator context and fetches its own
// data, so AggregatorPortfolio.tsx only needs to mount it as a tab.
//
// REAL DATA SOURCES — every plotted point/shape maps to one of these:
//   - GET /whitespace/portfolio          (entitlement — member roster, doc
//     counts via the shared aggregator-portfolio query, shared-in sources)
//   - GET /whitespace/portfolio_map      (Task #5126 — the joint 2-D layout)
//   - GET /whitespace/portfolio_topology (Task #5129 — validated holes/
//     bridges/frontier edges, used here for the candidate/discarded counts —
//     a real data-quality reading the mockup doesn't show but costs nothing
//     since the endpoint is already being called for the Atlas)
//   - GET /whitespace/portfolio_score    (Task #5130 — the SAME topology
//     candidates, now carrying wos_p_score; the geometry AND the "H1 ·
//     enclosed · 91"-style badges both come from these regions)
//   - GET /whitespace/portfolio_synergy  (Task #5119/#5122, already merged —
//     feeds the single "top synergy cell" into the shared ranked-item list)
//
// HONEST GAPS vs. the mockup (never fabricated — see lib/whitespace.ts's
// module comment for the same convention SynergyMatrix.tsx established):
//   - Corpus underlay (grey background dots/contours): no endpoint returns
//     public-corpus coordinates for a portfolio view — omitted, not faked.
//   - Shared-in violet diamonds: portfolio_projection.py's own docstring
//     confirms shared_in_sources are excluded from the projected point set
//     entirely (no document-level vector the disclosure ladder permits
//     fetching) — there is no position to plot. Shared-in sources are shown
//     as roster chips in the left panel instead of scattered points.
//   - The "N filings drifting in (90d)" encroachment arrow: no drift-
//     detection endpoint exists yet — omitted, not faked.
import { lazy, Suspense, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Map as MapIcon, Star, Layers, Box } from 'lucide-react'
import {
  getPortfolioEntitlement,
  getPortfolioMap,
  getPortfolioTopology,
  getPortfolioScore,
  getPortfolioSynergy,
  getPortfolioOverlap,
  getPortfolioShells,
  getAggregatorPortfolioProjects,
} from '../../services/api'

// Story #5305 Task #5339 — lazy, same rationale as WhiteSpaceTab.tsx's own
// `React.lazy(() => import('./Explorer3D'))`: three.js must never land in
// PortfolioAtlas's own eager chunk (Explorer3D.tsx's module docstring +
// vite.config.ts's manualChunks give it a named chunk), so this import is
// deferred until the '3d' lens is actually selected.
const Explorer3D = lazy(() => import('./Explorer3D'))
import {
  computeLayoutBounds,
  makeAtlasProjector,
  groupPortfolioLayoutByProject,
  computeCentroid2D,
  tracePortfolioFootprintLoop,
  disclosureLevelLabel,
  buildRankedWhitespaceItems,
  projectTagColor,
  computeGroupFootprints,
  portfolioProjectNames,
  type RankedWhitespaceItem,
  type OverlapFootprintEllipse,
} from '../../lib/whitespace'
import type { OverlapGroup } from '../../types/whitespace'
import { useAggregator } from '../../contexts/AggregatorContext'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'
import HelpPopover from '../help/HelpPopover'
import HowToReadThisCard from '../help/HowToReadThisCard'

// design §6.5: "the graph-overlay idea from the 3-D visualisation note,
// rendered as regions (with an edge-bundle mode past twelve projects)".
// Above this many entitled members, per-pair ellipse-intersection regions
// get too visually dense to read, so the lens switches to bundled edges
// between group-member centroids instead.
const OVERLAP_LENS_EDGE_BUNDLE_THRESHOLD = 12

const VIEWPORT = { width: 900, height: 560, padding: 44 }

const KIND_BADGE_COLOR: Record<string, { fill: string; text: string }> = {
  hole: { fill: '#FBBF24', text: '#111827' },
  bridge: { fill: '#FEF3C7', text: '#92400E' },
  frontier_edge: { fill: '#FBBF24', text: '#111827' },
}

interface PortfolioAtlasProps {
  onOpenTopologyStrategy?: () => void
  /** design §6.3 "cross-links both ways" — the Overlap lens's click-through
   * target (switches the caller to the Overlap tab with this group
   * selected). Omitted when unavailable rather than a dead click target. */
  onOpenOverlapGroup?: (groupId: string) => void
}

export default function PortfolioAtlas({ onOpenTopologyStrategy, onOpenOverlapGroup }: PortfolioAtlasProps) {
  const { activeAggregatorId } = useAggregator()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [lens, setLens] = useState<'topology' | 'overlap' | '3d'>('topology')
  // Bug #5898 (F5): this inline swatch legend sits directly above the map,
  // in the same header row as the lens tabs — with a large portfolio (the
  // review's own example had 19 members) it wrapped into a legend as tall
  // as the map itself. The "Portfolio members" card in the left sidebar
  // already lists every member legibly; this row is a quick colour key, not
  // the only place to find a name, so it caps by default and says how many
  // more there are in prose rather than growing without limit.
  const [showAllHeaderSwatches, setShowAllHeaderSwatches] = useState(false)
  const HEADER_LEGEND_CAP = 6

  const entitlementQuery = useQuery({
    queryKey: ['portfolio-entitlement', activeAggregatorId],
    queryFn: () => getPortfolioEntitlement(activeAggregatorId),
  })

  // Shares AggregatorPortfolio's/SynergyMatrix's exact query key — project
  // names + document counts cost no extra fetch.
  const projectsQuery = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
  })

  const mapQuery = useQuery({
    queryKey: ['portfolio-map', activeAggregatorId],
    queryFn: () => getPortfolioMap(activeAggregatorId),
  })

  const topologyQuery = useQuery({
    queryKey: ['portfolio-topology', activeAggregatorId],
    queryFn: () => getPortfolioTopology(activeAggregatorId),
  })

  const scoreQuery = useQuery({
    queryKey: ['portfolio-score', activeAggregatorId],
    queryFn: () => getPortfolioScore(activeAggregatorId),
  })

  // Shares SynergyMatrix's exact query key so React Query dedupes the request
  // when both tabs have been visited in the same session.
  const synergyQuery = useQuery({
    queryKey: ['portfolio-synergy', activeAggregatorId],
    queryFn: () => getPortfolioSynergy(activeAggregatorId),
  })

  // Shares OverlapView's/SynergyMatrix's exact query key — the Overlap lens
  // (design §6.5) costs no extra fetch once any Overlap surface has been
  // visited, and only actually fetches once this lens is selected.
  const overlapQuery = useQuery({
    queryKey: ['portfolio-overlap', activeAggregatorId],
    queryFn: () => getPortfolioOverlap(activeAggregatorId),
    // '3d' lens also needs the groups list (its shell-intersection tooltip
    // cross-references a real formed group — see Explorer3D's
    // findMatchingOverlapGroup) — same query key as OverlapView's/
    // SynergyMatrix's, so visiting either tab first costs no extra fetch.
    enabled: lens === 'overlap' || lens === '3d',
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3000 : false),
  })

  // Story #5305 Task #5339 — the 3-D Explorer's overlap-mode data (per-
  // project shells, full-space-confirmed intersections, stability-chipped
  // holes). Only fetched once the '3d' lens is actually opened — this is
  // the most expensive of the Atlas's own queries (a fresh 3-D projection +
  // per-candidate full-space validation), never fired for a viewer who
  // never opens the 3-D Explorer.
  const shellsQuery = useQuery({
    queryKey: ['portfolio-shells', activeAggregatorId],
    queryFn: () => getPortfolioShells(activeAggregatorId),
    enabled: lens === '3d',
  })

  const names = useMemo(
    () => portfolioProjectNames(projectsQuery.data),
    [projectsQuery.data],
  )
  const docCounts = useMemo(
    () => new Map((projectsQuery.data?.projects ?? []).map((p) => [p.project_id, p.document_count])),
    [projectsQuery.data],
  )

  const layout = mapQuery.data?.layout ?? {}
  const memberProjectIds = mapQuery.data?.member_project_ids ?? []
  const regions = scoreQuery.data?.regions ?? []

  const bounds = useMemo(() => computeLayoutBounds(layout), [layout])
  const projector = useMemo(() => makeAtlasProjector(bounds, VIEWPORT), [bounds])
  const grouped = useMemo(() => groupPortfolioLayoutByProject(layout, memberProjectIds), [layout, memberProjectIds])

  const footprintLoop = useMemo(() => {
    const frontierEdges = topologyQuery.data?.frontier_edges ?? []
    const loop = tracePortfolioFootprintLoop(frontierEdges, layout)
    return loop ? loop.map((p) => projector(p)) : null
  }, [topologyQuery.data, layout, projector])

  const rankedItems = useMemo(
    () =>
      buildRankedWhitespaceItems(
        regions,
        synergyQuery.data?.cells ?? [],
        synergyQuery.data?.pair_scores ?? [],
        names,
      ),
    [regions, synergyQuery.data, names],
  )
  const spatialItems = rankedItems.filter((i) => i.kind !== 'synergy' && i.centroid2d)
  const selected = selectedId ? rankedItems.find((i) => i.id === selectedId) ?? null : null

  // Overlap lens (design §6.5): every entitled member's footprint, reusing
  // GroupDetail's own ellipse geometry (computeGroupFootprints) across the
  // FULL member roster rather than one group's members.
  const footprints = useMemo(() => computeGroupFootprints(layout, memberProjectIds), [layout, memberProjectIds])
  const footprintsById = useMemo(() => new Map(footprints.map((f) => [f.project_id, f])), [footprints])
  // Group formation already gates at coefficient >= 40 (overlap_score.py's
  // form_groups) — every non-worth_a_look group qualifies as a "strong
  // intersection" for the lens without a second threshold check.
  const overlapGroups = (overlapQuery.data?.groups ?? []).filter((g) => !g.worth_a_look)
  const useEdgeBundleMode = memberProjectIds.length > OVERLAP_LENS_EDGE_BUNDLE_THRESHOLD

  const isLoading = mapQuery.isLoading || scoreQuery.isLoading || topologyQuery.isLoading || entitlementQuery.isLoading
  const isError = mapQuery.isError || scoreQuery.isError || topologyQuery.isError || entitlementQuery.isError

  if (isLoading) {
    return (
      <div data-testid="portfolio-atlas-loading">
        <LoadingSkeleton type="card" rows={3} />
      </div>
    )
  }

  if (isError) {
    const err = (mapQuery.error ?? scoreQuery.error ?? topologyQuery.error ?? entitlementQuery.error) as Error | null
    return (
      <div className="p-4 text-sm text-red-600" role="alert" data-testid="portfolio-atlas-error">
        Couldn't load the portfolio atlas: {err?.message ?? 'unknown error'}
      </div>
    )
  }

  if (Object.keys(layout).length === 0) {
    return (
      <EmptyState
        icon={<MapIcon className="w-7 h-7" />}
        title="Nothing to map yet"
        description="Your portfolio needs at least one entitled project with an embedded document before a joint atlas can be built."
      />
    )
  }

  const memberRows = [
    ...memberProjectIds.map((id, idx) => ({
      key: id,
      color: projectTagColor(id, memberProjectIds),
      name: names.get(id) ?? id,
      caption: `${docCounts.get(id) ?? grouped.get(id)?.length ?? 0} docs`,
      sharedIn: false,
      idx,
    })),
  ]
  const sharedInSources = entitlementQuery.data?.shared_in_sources ?? []

  return (
    <div data-testid="portfolio-atlas">
      <HowToReadThisCard
        visualisationId="portfolio-atlas"
        title="Portfolio Atlas"
        body="Every entitled document across your portfolio, jointly projected onto one map. Each project has its own colour and a footprint line tracing its own ground; amber markers are the same holes, bridges and frontier directions your project-level scans find."
        example="Two projects' footprint lines overlapping on the map is your first visual clue they're working the same ground — open the Overlap tab to see the evidence behind it."
      />
      <div className="flex gap-4 flex-col lg:flex-row">
        <div className="w-full lg:w-[280px] flex flex-col gap-3 flex-shrink-0">
          <div className="light-card p-4" data-tour="portfolio-atlas-members">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Portfolio members (what you can see)
            </div>
            <div className="mt-2.5 flex flex-col gap-1.5">
              {memberRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center gap-2 px-2 py-1.5 border border-sky-100 rounded-lg bg-white"
                >
                  <span className="w-3 h-3 rounded flex-shrink-0" style={{ background: row.color }} />
                  <span className="text-[12px] font-bold text-iflow-900 flex-1 truncate">{row.name}</span>
                  <span className="text-[10.5px] text-gray-500 flex-shrink-0">{row.caption}</span>
                </div>
              ))}
              {sharedInSources.map((source) => (
                <div
                  key={source.source_project_id}
                  className="flex items-center gap-2 px-2 py-1.5 border border-dashed border-violet-300 rounded-lg bg-white"
                >
                  <span className="w-3 h-3 rounded-full border-2 border-violet-500 bg-white flex-shrink-0" />
                  <span className="text-[12px] font-bold text-violet-700 flex-1 truncate">
                    Shared in · {source.org_name ?? source.source_org_id}
                  </span>
                  <span className="text-[10.5px] text-gray-500 flex-shrink-0">
                    {disclosureLevelLabel(source.disclosure_level)}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10.5px] text-gray-500 leading-relaxed mt-2">
              The atlas only ever contains vectors this account is entitled to see — own projects, org projects, and
              shared sources at their disclosure level. Shared-in sources carry no document-level position (the
              disclosure ladder never releases one), so they're listed here rather than scattered on the map.
            </p>
          </div>

          <div className="light-card p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Portfolio shape at a glance
              <HelpPopover topic="pf-shape" size={13} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2.5" data-tour="portfolio-shape-tiles">
              <ShapeTile value={topologyQuery.data?.holes.length ?? 0} label="Holes" tone="amber" />
              <ShapeTile value={topologyQuery.data?.bridges.length ?? 0} label="Bridges" tone="amber" />
              <ShapeTile value={memberProjectIds.length} label="Members" />
              <ShapeTile value={topologyQuery.data?.frontier_edges.length ?? 0} label="Frontier dirs" />
              <ShapeTile value={synergyQuery.data?.cross_project_cell_count ?? 0} label="Synergy cells" />
              <ShapeTile value={topologyQuery.data?.discarded_candidate_count ?? 0} label="2-D artifacts discarded" />
            </div>
            {onOpenTopologyStrategy && (
              <button
                onClick={onOpenTopologyStrategy}
                className="text-[12px] font-bold text-iflow-700 hover:text-iflow-800 mt-2.5"
              >
                Open Topology &amp; Strategy →
              </button>
            )}
          </div>
        </div>

        <div className="light-card flex-1 p-0 overflow-hidden" data-tour="portfolio-atlas-map">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-sky-100 flex-wrap">
            <h2 className="text-sm font-semibold text-iflow-900">One map of everything the portfolio knows</h2>
            <HelpPopover topic="pf-atlas" size={14} />
            <span className="text-[11px] text-gray-500">All visible vectors, jointly projected</span>

            {/* design §6.5: "the Portfolio Atlas gains an Overlap lens" —
                an alternate reading of the SAME map, not a separate view. */}
            <div className="flex gap-0.5 bg-sky-500/10 rounded-lg p-0.5" role="tablist" aria-label="Atlas lens">
              <button
                role="tab"
                aria-selected={lens === 'topology'}
                onClick={() => setLens('topology')}
                data-testid="atlas-lens-topology"
                className={`flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-medium rounded-md transition-colors ${
                  lens === 'topology' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MapIcon className="w-3 h-3" /> Topology
              </button>
              <button
                role="tab"
                aria-selected={lens === 'overlap'}
                onClick={() => setLens('overlap')}
                data-testid="atlas-lens-overlap"
                className={`flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-medium rounded-md transition-colors ${
                  lens === 'overlap' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Layers className="w-3 h-3" /> Overlap
              </button>
              <button
                role="tab"
                aria-selected={lens === '3d'}
                onClick={() => setLens('3d')}
                data-testid="atlas-lens-3d"
                className={`flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-medium rounded-md transition-colors ${
                  lens === '3d' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Box className="w-3 h-3" /> 3-D Explorer
              </button>
            </div>

            <div className="flex-1" />
            <div
              className="flex items-center gap-2.5 text-[10.5px] text-gray-500 flex-wrap"
              data-testid="atlas-header-legend"
            >
              {(showAllHeaderSwatches ? memberProjectIds : memberProjectIds.slice(0, HEADER_LEGEND_CAP)).map((id) => (
                <span key={id} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: projectTagColor(id, memberProjectIds) }} />
                  {names.get(id) ?? id}
                </span>
              ))}
              {!showAllHeaderSwatches && memberProjectIds.length > HEADER_LEGEND_CAP && (
                <button
                  type="button"
                  onClick={() => setShowAllHeaderSwatches(true)}
                  className="font-medium text-iflow-600 hover:text-iflow-800 hover:underline"
                  data-testid="atlas-legend-show-more"
                >
                  +{memberProjectIds.length - HEADER_LEGEND_CAP} more — see Portfolio members
                </button>
              )}
              {showAllHeaderSwatches && memberProjectIds.length > HEADER_LEGEND_CAP && (
                <button
                  type="button"
                  onClick={() => setShowAllHeaderSwatches(false)}
                  className="font-medium text-iflow-600 hover:text-iflow-800 hover:underline"
                >
                  Show fewer
                </button>
              )}
              {lens === 'topology' && (
                <>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-100 border border-amber-600 inline-block" /> Hole / bridge
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-white border border-dashed border-amber-600 inline-block" /> Frontier
                  </span>
                </>
              )}
              {lens === 'overlap' && (
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70 inline-block" />
                  {useEdgeBundleMode ? 'Overlap ≥40 (bundled edge)' : 'Overlap ≥40 (shared ground)'}
                </span>
              )}
            </div>
          </div>

          {lens === '3d' ? (
            <div className="p-3" data-testid="portfolio-atlas-3d">
              {shellsQuery.isLoading ? (
                <LoadingSkeleton type="card" rows={4} />
              ) : shellsQuery.isError ? (
                <div className="p-4 text-sm text-red-600" role="alert">
                  Couldn't load the 3-D Explorer: {(shellsQuery.error as Error | null)?.message ?? 'unknown error'}
                </div>
              ) : (
                <Suspense fallback={<LoadingSkeleton type="card" rows={4} />}>
                  <Explorer3D
                    layout={null}
                    overlapShells={shellsQuery.data ?? null}
                    overlapProjectNames={names}
                    overlapGroups={overlapQuery.data?.groups ?? null}
                    onOpenOverlapGroup={onOpenOverlapGroup}
                  />
                </Suspense>
              )}
            </div>
          ) : (
          <svg
            data-testid="portfolio-atlas-svg"
            viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`}
            className="w-full bg-white"
            style={{ aspectRatio: `${VIEWPORT.width} / ${VIEWPORT.height}` }}
          >
            <defs>
              <pattern id="atlas-hatch" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                <rect width="8" height="8" fill="rgba(251,191,36,.14)" />
                <line x1="0" y1="0" x2="0" y2="8" stroke="#D97706" strokeWidth="1.6" opacity=".55" />
              </pattern>
            </defs>

            {lens === 'topology' && footprintLoop && (
              <polygon
                points={footprintLoop.map(([x, y]) => `${x},${y}`).join(' ')}
                fill="rgba(14,165,233,.05)"
                stroke="#0284C7"
                strokeWidth={2.2}
                opacity={0.9}
              />
            )}

            {lens === 'overlap' &&
              footprints.map((f) => {
                const color = projectTagColor(f.project_id, memberProjectIds)
                const screen = projectEllipseToScreen(f, projector)
                return (
                  <ellipse
                    key={`footprint-${f.project_id}`}
                    data-testid={`overlap-lens-footprint-${f.project_id}`}
                    cx={screen.cx}
                    cy={screen.cy}
                    rx={screen.rx}
                    ry={screen.ry}
                    fill={color}
                    fillOpacity={0.14}
                    stroke={color}
                    strokeOpacity={0.55}
                    strokeWidth={1.4}
                  />
                )
              })}

            {memberProjectIds.map((projectId) => {
              const points = grouped.get(projectId) ?? []
              const color = projectTagColor(projectId, memberProjectIds)
              const centroid = computeCentroid2D(points)
              return (
                <g key={projectId}>
                  {points.map(([x, y], i) => {
                    const [sx, sy] = projector([x, y])
                    return <circle key={i} cx={sx} cy={sy} r={2.6} fill={color} opacity={0.65} />
                  })}
                  {centroid && (
                    <StarMark point={projector(centroid)} color={color} />
                  )}
                  {centroid && (
                    <text
                      x={projector(centroid)[0]}
                      y={projector(centroid)[1] + 26}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight={700}
                      fill="#334155"
                    >
                      {names.get(projectId) ?? projectId}
                    </text>
                  )}
                </g>
              )
            })}

            {lens === 'topology' &&
              spatialItems.map((item) => (
                <RegionMark
                  key={item.id}
                  item={item}
                  layout={layout}
                  projector={projector}
                  selected={selectedId === item.id}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}

            {lens === 'overlap' &&
              overlapGroups.map((group) =>
                useEdgeBundleMode ? (
                  <OverlapEdgeBundle
                    key={group.group_id}
                    group={group}
                    footprintsById={footprintsById}
                    projector={projector}
                    onSelect={() => onOpenOverlapGroup?.(group.group_id)}
                  />
                ) : (
                  <OverlapIntersectionRegion
                    key={group.group_id}
                    group={group}
                    footprintsById={footprintsById}
                    projector={projector}
                    onSelect={() => onOpenOverlapGroup?.(group.group_id)}
                  />
                ),
              )}
          </svg>
          )}

          {lens !== '3d' && (
          <div className="px-4 py-2 border-t border-sky-100 text-[10.5px] text-gray-500">
            {lens === 'topology' ? (
              <>
                Joint projection anchored on corpus landmarks · holes/bridges/frontier edges validated in the full
                1024-d embedding space before display ({topologyQuery.data?.candidate_count ?? 0} candidates found,{' '}
                {topologyQuery.data?.discarded_candidate_count ?? 0} discarded as 2-D-only projection artifacts)
              </>
            ) : overlapQuery.isLoading || overlapQuery.data?.status === 'processing' ? (
              'Comparing your portfolio for overlap — this can take a few minutes on a large portfolio.'
            ) : overlapGroups.length === 0 ? (
              'No scored (>=40) overlap groups found across this portfolio yet — footprints alone show entitled coverage.'
            ) : (
              `${overlapGroups.length} scored overlap group${overlapGroups.length === 1 ? '' : 's'} shaded above · click a region to open it in the Overlap tab`
            )}
          </div>
          )}
        </div>

        <div className="w-full lg:w-[280px] flex-shrink-0">
          <div className="light-card p-4" data-testid="portfolio-atlas-detail">
            {selected ? (
              <>
                <div className="flex items-center gap-2.5">
                  <span
                    className="flex items-center justify-center w-11 h-11 rounded-full text-[15px] font-extrabold flex-shrink-0"
                    style={{
                      background: KIND_BADGE_COLOR[selected.kind]?.fill ?? '#FBBF24',
                      color: KIND_BADGE_COLOR[selected.kind]?.text ?? '#111827',
                    }}
                  >
                    {selected.score}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-extrabold text-iflow-900">
                      {selected.id} <span className="text-[10px] font-bold text-amber-700">{selected.tagLabel}</span>
                    </div>
                    <div className="text-[10.5px] text-gray-500">
                      {selected.memberProjectIds.map((id) => names.get(id) ?? id).join(' · ')}
                    </div>
                  </div>
                </div>
                <p className="text-[11.5px] text-gray-700 mt-2.5 leading-relaxed">{selected.detail}</p>
              </>
            ) : (
              <p className="text-xs text-gray-500">Select a marker on the map to see its detail.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ShapeTile({ value, label, tone }: { value: number; label: string; tone?: 'amber' }) {
  return (
    <div className="border border-sky-100 rounded-lg bg-white px-2 py-2">
      <div className={`text-[16px] font-extrabold ${tone === 'amber' ? 'text-amber-700' : 'text-iflow-900'}`}>
        {value}
      </div>
      <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  )
}

function StarMark({ point, color }: { point: [number, number]; color: string }) {
  const [cx, cy] = point
  return (
    <g transform={`translate(${cx},${cy})`}>
      <Star
        x={-7}
        y={-7}
        width={14}
        height={14}
        color={color}
        fill={color}
        strokeWidth={1}
        stroke="#fff"
      />
    </g>
  )
}

function RegionMark({
  item,
  layout,
  projector,
  selected,
  onSelect,
}: {
  item: RankedWhitespaceItem
  layout: Record<string, [number, number]>
  projector: (p: [number, number]) => [number, number]
  selected: boolean
  onSelect: () => void
}) {
  if (!item.centroid2d) return null
  const [cx, cy] = projector(item.centroid2d)
  const points = (item.boundaryRecordIds ?? []).map((id) => layout[id]).filter(Boolean) as [number, number][]
  const screenPoints = points.map((p) => projector(p))

  return (
    <g
      onClick={onSelect}
      role="button"
      aria-label={`${item.id} — ${item.tagLabel} · score ${item.score}`}
      style={{ cursor: 'pointer' }}
    >
      {item.kind === 'hole' && screenPoints.length >= 3 && (
        <polygon
          points={screenPoints.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="url(#atlas-hatch)"
          stroke="#D97706"
          strokeWidth={selected ? 3 : 2.2}
        />
      )}
      {item.kind === 'bridge' && screenPoints.length >= 3 && (
        <polygon
          points={screenPoints.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="rgba(217,119,6,.18)"
          stroke="#D97706"
          strokeWidth={selected ? 3 : 1.6}
          strokeDasharray="6 5"
        />
      )}
      {item.kind === 'frontier_edge' && (
        <ellipse
          cx={cx}
          cy={cy}
          rx={46}
          ry={30}
          fill="rgba(251,191,36,.08)"
          stroke="#D97706"
          strokeWidth={selected ? 3 : 2}
          strokeDasharray="7 5"
        />
      )}
      <rect
        x={cx - (item.id.length > 2 ? 58 : 50)}
        y={cy - 11}
        width={item.id.length > 2 ? 116 : 100}
        height={22}
        rx={11}
        fill={item.kind === 'hole' || item.kind === 'frontier_edge' ? '#FBBF24' : '#FEF3C7'}
        stroke={item.kind === 'bridge' ? '#D97706' : 'none'}
        strokeWidth={1}
      />
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        fill={item.kind === 'bridge' ? '#92400E' : '#111827'}
      >
        {item.id} · {item.tagLabel === 'ENCLOSED HOLE' ? 'enclosed' : item.tagLabel.toLowerCase()} · {item.score}
      </text>
    </g>
  )
}

// ---------------------------------------------------------------------------
// Overlap lens (design §6.5) — Story #5305 Stream 14c, Task #5319
// ---------------------------------------------------------------------------

/** Projects a data-space footprint ellipse into screen space. `projector`
 * is affine but not necessarily axis-uniform in how callers might build it
 * in future, so radii are derived by projecting an edge point and measuring
 * the screen delta — correct regardless of the projector's internal scale
 * math, rather than duplicating makeAtlasProjector's own formula here. */
function projectEllipseToScreen(
  ellipse: OverlapFootprintEllipse,
  projector: (p: [number, number]) => [number, number],
): { cx: number; cy: number; rx: number; ry: number } {
  const [cx, cy] = projector([ellipse.cx, ellipse.cy])
  const rx = Math.abs(projector([ellipse.cx + ellipse.rx, ellipse.cy])[0] - cx)
  const ry = Math.abs(projector([ellipse.cx, ellipse.cy + ellipse.ry])[1] - cy)
  return { cx, cy, rx, ry }
}

/** A group's real member footprints resolved from the entitled-project
 * footprint map, in the group's own member order — a member with fewer
 * than 2 plotted points (computeFootprintEllipse's own honest-gap rule) is
 * simply absent, never a fabricated placeholder ellipse. */
function resolveGroupFootprints(
  group: OverlapGroup,
  footprintsById: Map<string, OverlapFootprintEllipse>,
): OverlapFootprintEllipse[] {
  return group.member_project_ids.map((id) => footprintsById.get(id)).filter((f): f is OverlapFootprintEllipse => !!f)
}

/** Compact, honest region label — member count + the group's real
 * strongest-pair score, never an invented group name (AI naming is a later
 * stream, not this one). */
function overlapLensLabel(group: OverlapGroup): string {
  return `${group.member_project_ids.length}-way · ${group.score}`
}

/** design §6.5: "strong intersections shaded amber with the group label,
 * click-through to the group" — the TRUE geometric intersection of every
 * member footprint, built from nested SVG clip-paths (each additional
 * member's ellipse clips the previous result), not an approximation. A
 * 2-member group needs one clip; an N-member group nests N-1. */
function OverlapIntersectionRegion({
  group,
  footprintsById,
  projector,
  onSelect,
}: {
  group: OverlapGroup
  footprintsById: Map<string, OverlapFootprintEllipse>
  projector: (p: [number, number]) => [number, number]
  onSelect: () => void
}) {
  const members = resolveGroupFootprints(group, footprintsById)
  if (members.length < 2) return null

  const screenEllipses = members.map((f) => projectEllipseToScreen(f, projector))
  const clipIds = screenEllipses.slice(1).map((_, i) => `overlap-clip-${group.group_id}-${i}`)
  const base = screenEllipses[0]

  let content = (
    <ellipse cx={base.cx} cy={base.cy} rx={base.rx} ry={base.ry} fill="#D97706" fillOpacity={0.4} />
  )
  for (let i = clipIds.length - 1; i >= 0; i--) {
    content = <g clipPath={`url(#${clipIds[i]})`}>{content}</g>
  }

  const centroidPoints = members.map((f) => [f.cx, f.cy] as [number, number])
  const centroid2d = computeCentroid2D(centroidPoints)
  const labelPoint = centroid2d ? projector(centroid2d) : [base.cx, base.cy]

  return (
    <g
      onClick={onSelect}
      role="button"
      aria-label={`Overlap group — ${overlapLensLabel(group)}`}
      data-testid={`overlap-lens-region-${group.group_id}`}
      style={{ cursor: 'pointer' }}
    >
      <defs>
        {screenEllipses.slice(1).map((e, i) => (
          <clipPath id={clipIds[i]} key={clipIds[i]}>
            <ellipse cx={e.cx} cy={e.cy} rx={e.rx} ry={e.ry} />
          </clipPath>
        ))}
      </defs>
      {content}
      <rect x={labelPoint[0] - 34} y={labelPoint[1] - 10} width={68} height={20} rx={10} fill="#FBBF24" />
      <text x={labelPoint[0]} y={labelPoint[1] + 4} textAnchor="middle" fontSize={10} fontWeight={800} fill="#111827">
        {overlapLensLabel(group)}
      </text>
    </g>
  )
}

/** design §6.5: "an edge-bundle mode past twelve projects" — above the
 * threshold, per-pair intersection shapes get too dense to read, so each
 * group draws as a single bundled curve between its member centroids
 * instead (a star from the group's own centroid to each member, rather
 * than an all-pairs fan, so an N-member group draws N edges, not N²). */
function OverlapEdgeBundle({
  group,
  footprintsById,
  projector,
  onSelect,
}: {
  group: OverlapGroup
  footprintsById: Map<string, OverlapFootprintEllipse>
  projector: (p: [number, number]) => [number, number]
  onSelect: () => void
}) {
  const members = resolveGroupFootprints(group, footprintsById)
  if (members.length < 2) return null

  const centroidPoints = members.map((f) => [f.cx, f.cy] as [number, number])
  const centroid2d = computeCentroid2D(centroidPoints)
  if (!centroid2d) return null
  const hub = projector(centroid2d)

  return (
    <g
      onClick={onSelect}
      role="button"
      aria-label={`Overlap group (bundled) — ${overlapLensLabel(group)}`}
      data-testid={`overlap-lens-bundle-${group.group_id}`}
      style={{ cursor: 'pointer' }}
    >
      {members.map((f) => {
        const [mx, my] = projector([f.cx, f.cy])
        const midX = (hub[0] + mx) / 2
        const midY = (hub[1] + my) / 2 - 14
        return (
          <path
            key={f.project_id}
            d={`M ${hub[0]} ${hub[1]} Q ${midX} ${midY} ${mx} ${my}`}
            fill="none"
            stroke="#D97706"
            strokeWidth={2.2}
            strokeOpacity={0.65}
            strokeDasharray="5 4"
          />
        )
      })}
      <circle cx={hub[0]} cy={hub[1]} r={12} fill="#FBBF24" stroke="#D97706" strokeWidth={1.4} />
      <text x={hub[0]} y={hub[1] + 3} textAnchor="middle" fontSize={9} fontWeight={800} fill="#111827">
        {group.score}
      </text>
      <text x={hub[0]} y={hub[1] - 16} textAnchor="middle" fontSize={9} fontWeight={700} fill="#92400E">
        {overlapLensLabel(group)}
      </text>
    </g>
  )
}
